import { useState } from "react"
import {
    Box,
    Checkbox,
    Chip,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { cloneRuleSet } from "routes/rule-sets/controller/clone-rule-set"
import { archiveRuleSet } from "routes/rule-sets/controller/archive-rule-set"
import { restoreRuleSet } from "routes/rule-sets/controller/restore-rule-set"
import { formatDate } from "../format-utils"
import { LinkedScenariosCell } from "../linked-scenarios-cell"

const ACTION_LABELS = { include: "Include All", exclude: "Exclude All" }

export function RuleSetRow({ item }) {
    const ruleCount = (item.rules || []).length
    const usedByCount = item.usedByCount || 0
    const [anchorEl, setAnchorEl] = useState(null)
    const { selectedIds, onToggleSelect, canManageOptimiser } = useBoundContext()
    const isSelected = selectedIds?.has(item.id)

    return (
        <ListItemButton
            data-cy="rule-set-row"
            sx={{
                px: 3,
                py: 2,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-of-type": { borderBottom: 0 },
                ...(item.isArchived && { opacity: 0.5 }),
            }}
            onClick={navigateToRuleSet}
        >
            <ListItemBox sx={{ gap: 3 }}>
                <If value={canManageOptimiser}>
                    <Checkbox
                        size="small"
                        checked={isSelected || false}
                        onChange={handleToggleCheck}
                        onClick={stopProp}
                        sx={{ p: 0, mr: -1, flexShrink: 0 }}
                    />
                </If>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" fontWeight={500}>
                            {item.name}
                        </Typography>
                        <If value={item.isArchived}>
                            <Chip
                                label="Archived"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                            />
                        </If>
                    </Stack>
                    <If value={item.description}>
                        <TruncatedTypography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>
                            {item.description}
                        </TruncatedTypography>
                    </If>
                </Box>
                <Box sx={{ width: 120 }}>
                    <Typography variant="body2" color="text.secondary">
                        {ACTION_LABELS[item.defaultAction] || item.defaultAction || "Include All"}
                    </Typography>
                </Box>
                <Box sx={{ width: 140 }}>
                    <LinkedScenariosCell scenarios={item.usedByScenarios} count={usedByCount} />
                </Box>
                <Box sx={{ width: 100, textAlign: "right" }}>
                    <Typography variant="body2">{ruleCount}</Typography>
                </Box>
                <Box sx={{ width: 120 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {formatDate(item.modified || item.created)}
                    </Typography>
                </Box>
                <If value={canManageOptimiser}>
                    <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
                        <IconButton
                            aria-label="More options"
                            data-cy="rule-set-row-menu"
                            size="small"
                            onClick={openMenu}
                        >
                            <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                            <If value={!item.isArchived}>
                                <MenuItem onClick={handleEdit}>
                                    <ListItemIcon>
                                        <Iconify icon="solar:pen-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Edit</ListItemText>
                                </MenuItem>
                            </If>
                            <MenuItem onClick={handleDuplicate}>
                                <ListItemIcon>
                                    <Iconify icon="solar:copy-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Duplicate</ListItemText>
                            </MenuItem>
                            <If value={!item.isArchived}>
                                <MenuItem onClick={handleArchive}>
                                    <ListItemIcon>
                                        <Iconify icon="solar:archive-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Archive</ListItemText>
                                </MenuItem>
                                <Else>
                                    <MenuItem onClick={handleRestore}>
                                        <ListItemIcon>
                                            <Iconify icon="solar:restart-bold" width={20} />
                                        </ListItemIcon>
                                        <ListItemText>Restore</ListItemText>
                                    </MenuItem>
                                </Else>
                            </If>
                        </Menu>
                    </Box>
                    <Else>
                        <Box sx={{ width: 40 }} />
                    </Else>
                </If>
            </ListItemBox>
        </ListItemButton>
    )

    function openMenu(e) {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }

    function closeMenu(e) {
        if (e) e.stopPropagation()
        setAnchorEl(null)
    }

    function stopProp(e) {
        e.stopPropagation()
    }

    function handleToggleCheck(e) {
        e.stopPropagation()
        onToggleSelect?.(item.id)
    }

    function navigateToRuleSet() {
        navigate(`/app/optimiser/rule-sets/${item.id}`)
    }

    function handleEdit(e) {
        e.stopPropagation()
        closeMenu()
        navigate(`/app/optimiser/rule-sets/${item.id}`)
    }

    async function handleDuplicate(e) {
        e.stopPropagation()
        closeMenu()
        const newSet = await busyWhile(duplicate, "Duplicating rule set...")
        if (newSet?.id) {
            navigate(`/app/optimiser/rule-sets/${newSet.id}`)
            successSnackbar("Rule set duplicated")
        }

        function duplicate() {
            return cloneRuleSet({ id: item.id })
        }
    }

    async function handleArchive(e) {
        e.stopPropagation()
        closeMenu()
        const usageWarning =
            usedByCount > 0
                ? `\n\nThis rule set is used by ${usedByCount} scenario variant${usedByCount > 1 ? "s" : ""}. Those variants will continue using it but it cannot be assigned to new variants.`
                : ""
        const ok = await confirm(
            `Archive "${item.name}"? It can be restored later from the Archived filter.${usageWarning}`,
            "Archive Rule Set"
        )
        if (!ok) return
        await busyWhile(() => archiveRuleSet({ id: item.id }), "Archiving rule set...")
        successSnackbar(`Rule set "${item.name}" archived`)
    }

    async function handleRestore(e) {
        e.stopPropagation()
        closeMenu()
        await busyWhile(() => restoreRuleSet({ id: item.id }), "Restoring rule set...")
        successSnackbar(`Rule set "${item.name}" restored`)
    }
}
